import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, Stack, Typography, CircularProgress, Alert } from "@mui/material";
import { delftBlue } from "@/theme/colors";
import GuideStatsCards from "./components/GuideStatsCards";
import GuideTable from "./components/GuideTable";
import CollapsibleSection from "./components/CollapsibleSection";
import {
  useGetGuidesQuery,
  useUpdateGuideStatusMutation,
} from "@/app/features/dashboard/dashboardApiSlice";
// import { useSelector } from "react-redux";
// import { selectUser } from "@/app/features/auth/authSlice";
// import Roles from "@/constants/roles";

const GuidesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // const user = useSelector(selectUser);

  // For Admin, don't pass companyId - backend will use their own company automatically
  const {
    data: guidesResponse,
    isLoading: isLoadingGuides,
    error: guidesError,
    refetch: refetchGuides,
  } = useGetGuidesQuery(undefined);

  // Refetch data when navigating back from form page
  useEffect(() => {
    if (location.state?.refresh) {
      refetchGuides();
      // Clear the state to prevent refetching on subsequent renders
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, refetchGuides, navigate, location.pathname]);

  const [updateGuideStatus] = useUpdateGuideStatusMutation();
  const [updatingGuideId, setUpdatingGuideId] = useState(null);

  const guides = guidesResponse?.data?.guides || [];

  // Calculate stats
  const stats = {
    total: guides.length,
    active: guides.filter((g) => g.isActive !== false).length,
    inactive: guides.filter((g) => g.isActive === false).length,
  };

  const handleInviteGuide = () => {
    navigate("/guides/add");
  };

  const handleView = (guide) => {
    // TODO: Implement view functionality if needed
    console.log("View guide:", guide);
  };

  const handleActivate = async (guide) => {
    const guideId = guide._id || guide.id;
    setUpdatingGuideId(guideId);
    try {
      await updateGuideStatus({
        userId: guideId,
        isActive: !guide.isActive,
      }).unwrap();
      refetchGuides();
    } catch (error) {
      console.error("Failed to update guide status:", error);
      alert(error?.data?.message || "Failed to update user status");
    } finally {
      setUpdatingGuideId(null);
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
        display: "flex",
        flexDirection: "column",
        gap: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Stack spacing={{ xs: 2, sm: 2.5 }} sx={{ alignSelf: "stretch" }}>
        {/* Header Section */}
        <Box
          sx={{
            alignSelf: "stretch",
            minHeight: { xs: "auto", sm: 60 },
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", sm: 331.6 },
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
              alignItems: "flex-start",
              gap: { xs: 0.5, sm: 0.75, md: 1 },
            }}
          >
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 22, sm: 24, md: 28 },
                fontFamily: "Audiowide, sans-serif",
                fontWeight: 400,
                lineHeight: 1.29,
                width: "100%",
              }}
            >
              Users
            </Typography>
            <Typography
              sx={{
                color: delftBlue[500],
                fontSize: { xs: 13, sm: 14, md: 16 },
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                lineHeight: 1.5,
                width: "100%",
                wordWrap: "break-word",
                overflowWrap: "break-word",
              }}
            >
              Manage and onboard users for your company
            </Typography>
          </Box>
        </Box>

        {/* Stats Cards */}
        <CollapsibleSection
          title="User Statistics"
          defaultExpanded={true}
          showCard={false}
        >
          {isLoadingGuides ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <CircularProgress />
            </Box>
          ) : guidesError ? (
            <Alert severity="error">
              {guidesError?.data?.message || "Failed to load statistics"}
            </Alert>
          ) : (
            <GuideStatsCards stats={stats} onInviteGuide={handleInviteGuide} />
          )}
        </CollapsibleSection>

        {/* Guides Table */}
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            gap: { xs: 3, sm: 4, md: 6 },
          }}
        >
          {isLoadingGuides ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <CircularProgress />
            </Box>
          ) : guidesError ? (
            <Alert severity="error">
              {guidesError?.data?.message || "Failed to load users"}
            </Alert>
          ) : (
            <GuideTable
              guides={guides}
              onView={handleView}
              onActivate={handleActivate}
              updatingGuideId={updatingGuideId}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
};

export default GuidesPage;
